import { ActivityEnrollableTimesModal_ActivityFragment } from "@outschool/gql-frontend-generated";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  TutorMatchEnrollmentDataContext,
  TutorMatchEnrollmentDataType,
} from "@outschool/ui-components-website";
import { useImpressionTracking } from "@outschool/ui-legacy-component-library";
import { useNavigation, useNodeRef } from "@outschool/ui-utils";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDebouncedCallback as useDebouncedCallbackVendor } from "use-debounce";

import TutoringEnrollmentModal from "../../../routes/TutoringEnrollmentModal";
import { EnrollmentFromStripePurchaseOrder } from "../../enrolling/EnrollmentMeetingPurchaseWithStripe";
import { useMembershipRequirement } from "../../subscriptions/hooks/useMembershipRequirement";

type TutorEnrollButtonAndModal = {
  activity: {
    uid: string;
    isSelfPaced?: boolean;
    subject: string | null;
    size_max: number | null;
  };
  buttonText: string;
  buttonSize?: "small" | "medium" | "large";
  activityWithEnrollableTimes?: ActivityEnrollableTimesModal_ActivityFragment;
};

export default function TutorEnrollButtonAndModal({
  activity,
  buttonText,
  buttonSize = "medium",
  activityWithEnrollableTimes,
}: TutorEnrollButtonAndModal) {
  const [tutorMatchEnrollmentData, setTutorMatchEnrollmentData] =
    React.useState<TutorMatchEnrollmentDataType | undefined>(undefined);

  const [tutorMatchActivityEnrollNode, tutorMatchActivityEnrollRef] =
    useNodeRef();
  const location = useLocation();
  const navigate = useNavigation();
  const { isLoggedIn } = useSession();
  const { mustPurchaseMembership, mustPurchaseEsaMembership } =
    useMembershipRequirement();

  const { trackTouch: trackTutorMatchEnroll } = useImpressionTracking({
    node: tutorMatchActivityEnrollNode,
    trackingLabel: "tutor_match_page_activity_enroll",
    sharedProperties: {
      activity_uid: tutorMatchEnrollmentData?.activity.uid,
      sectionUid: tutorMatchEnrollmentData?.section?.uid,
    },
    uniqueId: `tutor_match_page_activity_enroll-${tutorMatchEnrollmentData?.activity.uid}`,
  });

  // Auto-trigger button click logic if URL has /enrollNew
  useEffect(() => {
    const isEnrollNewPath = location.pathname.endsWith("/enrollNew");
    if (isEnrollNewPath) {
      handleTutorEnrollClick();
    }
    // Only run when path or search changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const [tutorMatchActivityEnrollmentOnComplete] = useDebouncedCallbackVendor(
    async (enrollments: EnrollmentFromStripePurchaseOrder[]) => {
      if (tutorMatchEnrollmentData) {
        const enrollment = enrollments[0];
        if (enrollment) {
          // track learner planner enrollment
          trackTutorMatchEnroll({
            enrollmentUid: enrollment.uid,
            activityUid: tutorMatchEnrollmentData.activity.uid,
            sectionUid: enrollment.section_uid,
          });
        }

        enrollment &&
          setTutorMatchEnrollmentData?.({
            ...tutorMatchEnrollmentData,
            section: { uid: enrollment?.section_uid! },
          });

        return Promise.resolve();
      }
    },
    500,
  );

  const handleTutorEnrollClick = () => {
    if (isLoggedIn) {
      if (mustPurchaseMembership || mustPurchaseEsaMembership) {
        // Remove '/enrollNew' from the pathname if present before constructing the navigation path using regex
        const basePath = location.pathname.replace(/\/enrollNew$/, "");
        navigate(
          `${basePath}?joinMembershipModal=true&redirectAfterPath=${basePath}/enrollNew`,
          {
            replace: true,
          },
        );
      } else {
        setTutorMatchEnrollmentData?.({
          activity,
        });
      }
    } else {
      setTutorMatchEnrollmentData?.({
        activity,
      });
    }
  };

  return (
    <TutorMatchEnrollmentDataContext.Provider
      value={{
        tutorMatchEnrollmentData,
        setTutorMatchEnrollmentData,
      }}
    >
      <TrackedButton
        variant="contained"
        fullWidth
        size={buttonSize}
        onClick={handleTutorEnrollClick}
        trackingName={"tutor_calendar_enrollment_button"}
        trackingUniqueId={activity.uid}
        trackingSharedProperties={{
          activity_uid: activity.uid,
        }}
      >
        {buttonText}
      </TrackedButton>
      {tutorMatchEnrollmentData && (
        <TutoringEnrollmentModal
          ref={tutorMatchActivityEnrollRef}
          // REFACTOR: Technically don't need these anymore since we are passing them in context provider.
          tutorMatchEnrollmentData={tutorMatchEnrollmentData}
          onCancel={() => {
            setTutorMatchEnrollmentData(undefined);
          }}
          onPaymentComplete={tutorMatchActivityEnrollmentOnComplete}
          activityWithEnrollableTimes={activityWithEnrollableTimes}
        />
      )}
    </TutorMatchEnrollmentDataContext.Provider>
  );
}
